import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, LayoutGrid } from 'lucide-react';
import {
  DECOR_NOTE_MAX,
  FURNITURE_BY_ID,
  formatPence,
  isNewPiece,
  lookCostOf,
  placementProblem,
  type DecorLayout,
  type HustleAction,
  type HustleDecorView,
  type LifeView,
  type Rotation,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { toPreviewCharacter } from '@london-life/game/appearance';
import type { RoomThing } from '@london-life/game/house/room';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { useBackLayer } from '../lib/back-stack';
import { londonWhen } from '../lib/format';
import { HouseView } from '../house/house-view';
import { Catalogue, SelectedBar, freeSpot, placedOf } from '../house/buy-panel';

const priceOf = (item: string) => FURNITURE_BY_ID.get(item)?.pricePence;
const newId = () => `new-${Math.random().toString(36).slice(2, 10).padEnd(8, '0')}`;
// a look as the room draws it
const roomOf = (look: DecorLayout, things: HustleDecorView['things']): RoomThing[] => {
  const items = new Map<string, string>([
    ...things.map((thing): [string, string] => [thing.id, thing.item]),
    ...look.fresh.map((piece): [string, string] => [piece.id, piece.item]),
  ]);
  return look.placed.flatMap((thing) => {
    const item = items.get(thing.id);
    return item ? [{ id: thing.id, item, x: thing.x, z: thing.z, rotation: thing.rotation as Rotation }] : [];
  });
};

// A decorate job's copy of the poster's home (as Lagos Life's): the decorator works on it in Buy mode from
// anywhere (the draft saves itself, new pieces come out of the poster's budget) and sends it as a look; the
// poster reviews the look in the same room and accepts it, sends a note asking for changes, or turns it down.
export function DecorScreen({
  jobId,
  life,
  dataSaver,
  onClose,
  onChanged,
}: {
  jobId: string;
  life: LifeView;
  dataSaver?: boolean;
  onClose: () => void;
  // the job changed (a look sent or answered): the Hustle app reloads
  onChanged: () => void;
}) {
  const [view, setView] = useState<HustleDecorView | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState<RoomThing[]>([]);
  const [fresh, setFresh] = useState<DecorLayout['fresh']>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [catalogue, setCatalogue] = useState(false);
  const [revision, setRevision] = useState(0);
  const [note, setNote] = useState('');
  const [noting, setNoting] = useState(false);
  // the copy as the server last had it: where the room goes back to when a change is refused
  const accept = useCallback((next: HustleDecorView) => {
    setView(next);
    const look = next.role === 'worker' ? (next.draft ?? { placed: [], fresh: [] }) : next.look;
    if (next.role === 'worker' && !next.draft) {
      setPlaced(placedOf(next.things));
      setFresh([]);
    } else if (look) {
      setPlaced(roomOf(look, next.things));
      setFresh(look.fresh);
    } else setPlaced(placedOf(next.things));
  }, []);
  const load = useCallback(() => {
    setFailure(null);
    api.hustle
      .look(jobId)
      .then(accept)
      .catch((error: unknown) => setFailure(describeError(error)));
  }, [accept, jobId]);
  useEffect(load, [load]);
  useBackLayer(true, onClose);

  const worker = view?.role === 'worker';
  const editing = Boolean(worker && view?.canEdit);
  const cost = lookCostOf({ placed: [], fresh }, priceOf);
  const left = (view?.budgetPence ?? 0) - cost;
  const items = useMemo(() => new Map(fresh.map((piece) => [piece.id, piece.item])), [fresh]);
  const stored = (view?.things ?? []).filter((thing) => !placed.some((entry) => entry.id === thing.id));
  const selectedThing = placed.find((thing) => thing.id === selected) ?? null;
  const selectedItem = selectedThing ? FURNITURE_BY_ID.get(selectedThing.item) : null;

  // every change saves the draft quietly; a refused one puts the room back as it was saved
  const commit = (nextPlaced: RoomThing[], nextFresh = fresh) => {
    setPlaced(nextPlaced);
    setFresh(nextFresh);
    setBusy(true);
    api.hustle
      .saveLook(jobId, {
        placed: nextPlaced.map(({ id, x, z, rotation }) => ({ id, x, z, rotation })),
        fresh: nextFresh.filter((piece) => nextPlaced.some((thing) => thing.id === piece.id)),
      })
      .then((next) => setView(next))
      .catch((error: unknown) => {
        notify(describeError(error).message);
        if (view) accept(view);
      })
      .finally(() => setBusy(false));
  };
  const tryLayout = (next: RoomThing[], nextFresh = fresh) => {
    if (!view) return;
    const problem = placementProblem(view.room.floor, next);
    if (problem) {
      notify(problem);
      setRevision((value) => value + 1);
      return;
    }
    commit(next, nextFresh);
  };
  const drop = (id: string, x: number, z: number) =>
    tryLayout(placed.map((thing) => (thing.id === id ? { ...thing, x, z } : thing)));
  const turn = () =>
    selectedThing &&
    tryLayout(
      placed.map((thing) =>
        thing.id === selectedThing.id ? { ...thing, rotation: ((thing.rotation + 1) % 4) as Rotation } : thing,
      ),
    );
  // a new piece stored is a new piece left out of the look
  const store = () => {
    if (!selectedThing) return;
    commit(
      placed.filter((thing) => thing.id !== selectedThing.id),
      fresh.filter((piece) => piece.id !== selectedThing.id),
    );
    setSelected(null);
  };
  const place = (thing: { id: string; item: string }, nextFresh = fresh) => {
    const item = FURNITURE_BY_ID.get(thing.item);
    if (!view || !item) return;
    const spot = freeSpot(view.room.floor, placed, item);
    if (!spot) return notify(`No room for ${item.label}. Move something first.`);
    commit([...placed, { id: thing.id, item: item.id, ...spot, rotation: 0 }], nextFresh);
    setSelected(thing.id);
  };
  const act = async (body: HustleAction, after?: () => void) => {
    setBusy(true);
    try {
      const reply = await api.hustle.act(jobId, body);
      if (reply.message) notify(reply.message);
      onChanged();
      after?.();
      load();
    } catch (error) {
      notify(describeError(error).message);
    } finally {
      setBusy(false);
    }
  };

  const title = view ? (worker ? `Decorating ${view.poster}'s home` : `${view.worker}'s look`) : 'The look';
  const lookCost = view?.lookCostPence ?? 0;
  const ownCount = view?.look ? view.look.placed.filter((thing) => !isNewPiece(thing.id)).length : 0;
  return (
    <div className="venue-screen house-screen buying decor-screen" role="dialog" aria-modal="true" aria-label={title}>
      {view && (
        <HouseView
          room={view.room}
          placed={placed}
          character={toPreviewCharacter(life.appearance, life.firstName)}
          dataSaver={dataSaver}
          editing={editing}
          selectedId={selected}
          onSelect={setSelected}
          onDrop={drop}
          revision={revision}
          onThing={() => null}
          lift={catalogue || selected ? 0.42 : 0.3}
        />
      )}
      {failure && (
        <div className="house-failure">
          <ErrorNotice error={failure} onRetry={load} />
        </div>
      )}
      {!view && !failure && (
        <p className="house-hint" role="status">
          Opening the door…
        </p>
      )}
      <div className="buy-top">
        <span className="buy-title" title={title}>
          🛋️ {worker ? 'Decorating' : title}
        </span>
        {view && worker && view.budgetPence > 0 && (
          <span className="buy-wallet" aria-label="Budget left">
            {formatPence(left)} left
          </span>
        )}
        <button
          type="button"
          className="buy-done"
          aria-label={worker ? 'Stop decorating' : 'Close the look'}
          onClick={onClose}
        >
          <Check size={18} aria-hidden="true" /> Done
        </button>
      </div>

      {/* the decorator: the thing picked up, the catalogue, or the bar to send the look */}
      {view && worker && editing && selectedThing && selectedItem ? (
        <SelectedBar
          item={selectedItem}
          line={
            items.has(selectedThing.id)
              ? `New piece · ${formatPence(selectedItem.pricePence)} from the budget`
              : `${view.poster}'s · Drag it to move it`
          }
        >
          <div className="buy-actions">
            <button type="button" onClick={turn} disabled={busy}>
              ⟳ Turn
            </button>
            <button type="button" onClick={store} disabled={busy}>
              {items.has(selectedThing.id) ? '✕ Leave out' : '▣ Store'}
            </button>
            <button type="button" onClick={() => setSelected(null)}>
              Close
            </button>
          </div>
        </SelectedBar>
      ) : view && worker && editing && catalogue ? (
        <Catalogue
          inRoom={placed}
          stored={stored}
          busy={busy}
          price={(item) => ({ label: `Add ${formatPence(item.pricePence)}`, short: item.pricePence > left })}
          onPick={setSelected}
          onPlace={(thing) => place(thing)}
          onBuy={
            view.budgetPence > 0
              ? (item) => {
                  if (fresh.length >= view.rules.piecesMax)
                    return notify(`A look can have up to ${view.rules.piecesMax} new pieces.`);
                  const piece = { id: newId(), item: item.id };
                  place(piece, [...fresh, piece]);
                }
              : undefined
          }
          shopNote="No budget for new pieces: their things only."
          onHide={() => setCatalogue(false)}
          hint={
            view.budgetPence > 0
              ? `New pieces come out of ${view.poster}'s budget (${formatPence(view.budgetPence)}). They pay only if they accept the look.`
              : `Move ${view.poster}'s things about, and bring stored things out. Nothing changes in their home until they accept.`
          }
        />
      ) : (
        view &&
        worker && (
          <section className="venue-bar decor-bar" aria-label="The look">
            <p className="decor-status">
              {view.state === 'changes' && view.note
                ? `✏️ ${view.poster} asked for changes: “${view.note}”`
                : view.state === 'turned_down'
                  ? `${view.poster} passed on your last look: try another`
                  : view.state === 'sent' && view.status !== 'hired'
                    ? `📨 Look sent: waiting for ${view.poster}`
                    : view.state === 'accepted'
                      ? `✨ ${view.poster} accepted your look`
                      : view.draft
                        ? `Draft saved · ${view.poster}'s ${view.homeLabel.toLowerCase()}`
                        : `A copy of ${view.poster}'s ${view.homeLabel.toLowerCase()}. Nothing changes in their home until they accept your look.`}
            </p>
            <p className="decor-status">
              {!view.keys
                ? `🔒 ${view.poster} took the keys back for now. Your draft is kept.`
                : view.status === 'hired'
                  ? `🔓 From anywhere until ${londonWhen(view.accessUntil)}${cost ? ` · new pieces ${formatPence(cost)}` : ''}`
                  : ''}
            </p>
            <div className="buy-actions">
              {editing && (
                <button type="button" onClick={() => setCatalogue(true)}>
                  <LayoutGrid size={16} aria-hidden="true" /> Catalogue
                </button>
              )}
              {view.moreUntil && (
                <button type="button" disabled={busy} onClick={() => void act({ action: 'more_time' })}>
                  ⏳ More time
                </button>
              )}
              {editing && (
                <button
                  type="button"
                  className="save"
                  disabled={busy || !view.draft}
                  onClick={() => void act({ action: 'send_look' })}
                >
                  📨 Send the look
                </button>
              )}
            </div>
          </section>
        )
      )}

      {/* the poster: the look, and the answer */}
      {view && !worker && (
        <section className="venue-bar decor-bar" aria-label="Your answer">
          {view.look ? (
            <>
              <p className="decor-status">
                {ownCount} of your things
                {view.look.fresh.length
                  ? ` · ${view.look.fresh.length} new piece${view.look.fresh.length === 1 ? '' : 's'} for ${formatPence(lookCost)}`
                  : ''}
                . Anything not in the look goes to storage.
              </p>
              {view.state === 'accepted' && <p className="decor-status">✨ This look is in your home.</p>}
              {view.state === 'sent' && view.status === 'paid' && (
                <p className="decor-status">
                  You've paid them already. Accept the look to put it in your home (you only pay for any new pieces).
                </p>
              )}
              {view.state === 'sent' && view.status !== 'paid' && (
                <p className="decor-status">
                  Accepting pays {view.worker} the fee. You can ask for changes {view.asksLeft} more time
                  {view.asksLeft === 1 ? '' : 's'}.
                </p>
              )}
              {view.state === 'sent' && (
                <>
                  {noting && (
                    <label className="railco-field">
                      <span>YOUR NOTE</span>
                      <textarea
                        value={note}
                        rows={2}
                        maxLength={DECOR_NOTE_MAX}
                        placeholder="Move the sofa under the window"
                        onChange={(event) => setNote(event.target.value)}
                      />
                      <small>
                        {note.trim().length}/{DECOR_NOTE_MAX}
                      </small>
                    </label>
                  )}
                  <div className="buy-actions">
                    {view.status === 'done' && view.asksLeft > 0 && (
                      <>
                        <button
                          type="button"
                          disabled={busy || (noting && note.trim().length < 2)}
                          onClick={() =>
                            noting
                              ? void act({ action: 'changes', version: view.version, note: note.trim() }, () =>
                                  setNoting(false),
                                )
                              : setNoting(true)
                          }
                        >
                          ✏️ {noting ? 'Send note' : 'Ask for changes…'}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void act({ action: 'turn_down', version: view.version })}
                        >
                          Turn down
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="save"
                      disabled={busy || view.walletPence < lookCost}
                      onClick={() => void act({ action: 'accept_look', version: view.version }, onClose)}
                    >
                      {view.walletPence < lookCost
                        ? `Need ${formatPence(lookCost)}`
                        : lookCost
                          ? `Accept · pay ${formatPence(lookCost)}`
                          : 'Accept the look'}
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <p className="decor-status">No look yet: {view.worker} is still working on it.</p>
          )}
        </section>
      )}
    </div>
  );
}
